import { useRefs } from 'contexts/RefContext';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import Konva from 'konva';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Circle } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { findLinkedPoints, getOverlappingPoints, moveVertexInward, roundPoint, snapNearbyPointsToTargetPoint } from '../utils';

import { validateDuringDrag } from './polygonValidator';
import { EDITED_FACET_HIGHLIGHT_COLOR, INVAILD_FACET_HIGHLIGHT, POINT_SNAP_THRESHOLD } from '../constant';
import { FACET_HIGHLIGHT_COLOR } from '../../../../constants';
import { deletePointsBatch, EDIT_POINT_ACTION, setEditableFacets, setPointSelected } from 'store/slices/RoofEditSlice';
import { setActiveDragFunctionality, setMapOnlyFrozenPoint } from 'store/slices/ToolSlice';

const BASE_RADIUS = 7;
const DRAG_FUNCTIONALITY = 'editFacetPoint';
interface Props extends Konva.LineConfig {
	segmentId: string;
	points: number[];
	index: number;
	currentSegment: editableFacet;
}

const EditablePoints = (props: Props) => {
	const { points, segmentId, index, currentSegment} = props;
	const { konvaRef } = useRefs();
	const isDraggingRef = useRef(false);
	const linkedPointsRef = useRef<{
		segmentId: string;
		pointIndex: number;
		facetLines: Konva.Line;
		dotNode: Konva.Circle;
	}[]>([]);
	const allCircles = useRef<Konva.Circle[]>([]);
	const scale = useKonvaImageScale();
	const dispatch = useDispatch();
	const { editableFacets, editPointAction, invalidFacetIDs, enableMergeFacets } = useSelector((state: RootState) => state.roofEditData.data);
	const activeLinkedIdsRef = useRef<Set<number>>(new Set());

	const transformedPoints = useMemo(() =>
		points.map((point, index) => index % 2 === 0 ? point * scale.x : point * scale.y),
	[points, scale.x, scale.y,]);

	const currentDotRef = useRef<Konva.Circle | null>(null);
	const { zoomLevel, hideSatelliteOverlay, activeDragFunctionality, mapOnlyFrozenPoint } = useSelector((state: RootState) => state.toolNewPostions.data);
	const wasHiddenRef = useRef(hideSatelliteOverlay);

	// When map only turns on mid-drag, freeze this point: snapshot its exact position into
	// the global store and pin the node there via dragBoundFunc (the drag session is never
	// stopped, so no re-click is needed — Konva keeps tracking the mouse but the pin keeps
	// the point at its frozen value). On exit, restore the snapshotted value from the store
	// and remove the pin — the point sits at its last frozen position until the user moves
	// the mouse, at which point it follows the mouse again naturally.
	useEffect(() => {
		const wasHidden = wasHiddenRef.current;
		wasHiddenRef.current = hideSatelliteOverlay;
		if (!isDraggingRef.current || activeDragFunctionality !== DRAG_FUNCTIONALITY) return;
		const dot = currentDotRef.current;
		if (!dot) return;

		if (hideSatelliteOverlay && !wasHidden) {
			const frozenPos = dot.getAbsolutePosition();
			dot.dragBoundFunc(() => frozenPos);
			dispatch(setMapOnlyFrozenPoint(frozenPos));
			return;
		}

		if (!hideSatelliteOverlay && wasHidden) {
			if (mapOnlyFrozenPoint) dot.setAbsolutePosition(mapOnlyFrozenPoint);
			dot.dragBoundFunc(undefined as any);
			dispatch(setMapOnlyFrozenPoint(null));
			dot.getLayer()?.batchDraw();
		}
	}, [hideSatelliteOverlay, activeDragFunctionality, mapOnlyFrozenPoint, dispatch]);

	const isDeletePointsAction = editPointAction === EDIT_POINT_ACTION.DELETE_POINT;

	const overlappingPoints: Array<{point: [number, number], overlappedWith: {[segmentId: string]: Array<[number, number]>}}> = useMemo(() => {
		if (!currentSegment) return [];

		return getOverlappingPoints(currentSegment, editableFacets, zoomLevel);
	}, [currentSegment, editableFacets, zoomLevel]);

	const isCommonPoint = Object.keys((overlappingPoints[index / 2]?.overlappedWith)?? {})?.length !== 0;
	const rafMap = useRef<WeakMap<Konva.Circle, number>>(new WeakMap());

	const handleDotDragStart = useCallback((e: Konva.KonvaEventObject<DragEvent>, index: number) => {
		if (!konvaRef?.current) return;

		const circle = e.target as Konva.Circle;
		const container = circle?.getStage()?.container();
		if (container) container.style.cursor = 'default';

		isDraggingRef.current = true;
		dispatch(setActiveDragFunctionality(DRAG_FUNCTIONALITY));
		circle.dragBoundFunc(undefined as any);

		const x = points[index];
		const y = points[index + 1];

		allCircles.current = konvaRef?.current?.find('.movable-circle') as Konva.Circle[];
		linkedPointsRef.current = findLinkedPoints(editableFacets, x, y, konvaRef, zoomLevel);
		linkedPointsRef.current.forEach(ref => {
			const { dotNode } = ref;
			if (dotNode) {
				dotNode.hide();
			}
		});
		activeLinkedIdsRef.current = new Set(
			linkedPointsRef.current.map(link => link.dotNode?._id).filter(Boolean)
		);
		e.target.show();
	},[dispatch, editableFacets, konvaRef, points, zoomLevel]);

	const handleDotDragMove = useCallback((e: Konva.KonvaEventObject<DragEvent>, index: number) => {
		let targetDotPosition = e.target.position();
		let absDotPosition = e.target.getAbsolutePosition(); // stage coords of dot
		const editFacetLineGroup = konvaRef?.current?.findOne(`.edit-facet-line-${segmentId}`);
		
		if(!editFacetLineGroup) return;

		const currentLine = konvaRef?.current?.findOne(`#editable-${segmentId}`) as Konva.Line;
		if(!currentLine) return;
		const isValidMove = validateDuringDrag(segmentId, currentLine, editableFacets, konvaRef!);
	
		if(!isValidMove){
			currentLine.fill(INVAILD_FACET_HIGHLIGHT);
			currentLine.stroke(INVAILD_FACET_HIGHLIGHT);
			currentLine.setAttr('valid', false);
		}else{
			currentLine.fill(currentLine.getAttr('isEdited') ? EDITED_FACET_HIGHLIGHT_COLOR : FACET_HIGHLIGHT_COLOR);
			currentLine.stroke('orange');
			currentLine.setAttr('valid', true);
		}
		
		const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

		// Increase snap tolerance for touch
		const SNAP_RADIUS = isTouch 
			? 2 * (BASE_RADIUS/ zoomLevel)
			: 2 * (BASE_RADIUS/ zoomLevel);

		for (const dot of allCircles.current) {
			if (dot._id === e.target._id || linkedPointsRef.current.some(link => link.dotNode?._id === dot._id)) continue;

			const currentDotPosition = dot.position();
			const otherX = currentDotPosition.x;
			const otherY = currentDotPosition.y;

			const dx = otherX - targetDotPosition.x;
			const dy = otherY - targetDotPosition.y;
			const distance = Math.hypot(dx, dy);

			if (distance < SNAP_RADIUS) {
				targetDotPosition = currentDotPosition;
				absDotPosition = dot.getAbsolutePosition();
				break;
			}
		}

		const linkedLinesPOstion = editFacetLineGroup.getAbsoluteTransform().copy().invert().point(absDotPosition);

		linkedPointsRef.current.forEach(ref => {
			const { facetLines } = ref;
			if (facetLines) {
				const linePoints = facetLines.points();
				linePoints[ref.pointIndex] = linkedLinesPOstion.x;
				linePoints[ref.pointIndex + 1] = linkedLinesPOstion.y;
				facetLines.points(linePoints);
			}
		});
		e.target.position(targetDotPosition);

		const layer = e.target.getLayer();
		if (layer) layer.batchDraw();
	},[editableFacets, konvaRef, segmentId, zoomLevel]);

	const handleDragEnd = useCallback((e: Konva.KonvaEventObject<DragEvent>, index: number) => {
		dispatch(setPointSelected({isSelected: false}));
		dispatch(setActiveDragFunctionality(null));
		dispatch(setMapOnlyFrozenPoint(null));
		isDraggingRef.current = false;
		const circle = e.target as Konva.Circle;
		circle.dragBoundFunc(undefined as any);
		const container = circle?.getStage()?.container();
		if (container) container.style.cursor = isCommonPoint? 'pointer': 'default';

		const effectedFacets: string[] = [];
		linkedPointsRef.current.forEach(ref => {
			const { dotNode } = ref;
			if (dotNode) {
				dotNode.show();
				dotNode.position(circle.position());
			}
			effectedFacets.push(ref.segmentId);
		});

		const updatedFacets = editableFacets.map(facet => {
			const facetLine = konvaRef?.current?.findOne(`#editable-${facet.segmentId}`) as Konva.Line;
			if (!facetLine) return facet;

			const occurance: Record<string, number> = {};

			const rawPoints = [];
			const pts = facetLine.points();

			for (let i = 0; i < pts.length; i += 2) {
				const x = (pts[i] / scale.x);
				const y = pts[i + 1] / scale.y;

				const key = `${x.toFixed(3)}-${y.toFixed(3)}`; // rounded to avoid floating point noise
				occurance[key] = (occurance[key] || 0) + 1;

				if (occurance[key] <= 2) {
					rawPoints.push(roundPoint([x, y]));
				}
			}

			return {
				...facet,
				coordinates: rawPoints,
				isEdited: facet.isEdited || effectedFacets.includes(facet.segmentId),
			};
		});
		dispatch(setEditableFacets({ editableFacets: updatedFacets }));
		allCircles.current = [];
		linkedPointsRef.current = [];
		activeLinkedIdsRef.current = new Set();
	},[dispatch, editableFacets, isCommonPoint, konvaRef, scale.x, scale.y]);

	function handleDotClick(e: Konva.KonvaEventObject<MouseEvent | Event>, index: number) {
		//e.target.getLayer()?.toggleHitCanvas();
		e.evt.preventDefault();
		e.cancelBubble = false;
		if (!konvaRef) return;

		if (isDeletePointsAction || (e.evt instanceof MouseEvent && e.evt.button === 2)) {
			const x = points[index];
			const y = points[index + 1];
			const linkedPoints = findLinkedPoints(editableFacets, x, y, konvaRef, zoomLevel);
			const deletions = linkedPoints.map(link => ({
				pointIndex: link.pointIndex,
				segmentId: link.segmentId,
			}));
			dispatch(deletePointsBatch(deletions));
			return;
		}

		if (!isCommonPoint) return;

		// for debug:
		// const facetLine = konvaRef?.current?.findOne(`#${currentSegment.segmentId}`) as Konva.Line;
		// // facetLine?.fill('red');
		// // console.log(currentSegment);
		// // re/turn;
		const updatedFacets = editableFacets.map(facet => {
			const commonPoints = overlappingPoints[index / 2].overlappedWith?.[facet.segmentId] ?? [];
			if (commonPoints.length) {
				let updatedCoords: number[][] = [];
				commonPoints.forEach(([tx, ty]) => {
					const idx = facet?.coordinates?.findIndex(([x, y]) => x === tx && y === ty);
					if (idx !== -1) {
						updatedCoords = moveVertexInward(facet.coordinates, idx, 5);
						updatedCoords = snapNearbyPointsToTargetPoint(updatedCoords, idx);
					}
				});

				return {
					...facet,
					coordinates: updatedCoords,
					isEdited: true,
				};
			}

			if (segmentId === facet.segmentId) {
				let updatedCoords = moveVertexInward(currentSegment.coordinates, index / 2, 5);
				updatedCoords = snapNearbyPointsToTargetPoint(updatedCoords, index / 2);

				return {
					...facet,
					coordinates: updatedCoords,
					isEdited: true
				};
			}
			return facet;
		});

		dispatch(setEditableFacets({ editableFacets: updatedFacets }));

		const circle = e.target as Konva.Circle;
		const container = circle?.getStage()?.container();

		if (container) container.style.cursor = 'default';
	}

	function handleHoverOnDot(e: Konva.KonvaEventObject<MouseEvent>, event: 'ENTER'|'OUT'){
		e.cancelBubble = true;
		if (!isCommonPoint) return;
		const circle = e.target as Konva.Circle;
		const container = circle?.getStage()?.container();
		// circle?.getLayer()?.toggleHitCanvas();
		if (container) container.style.cursor = (event === 'ENTER') ? 'pointer' : 'default';
	}

	const dotFillColor = useMemo(() => {
		switch (true) {
		case isCommonPoint:
			return '#2196f3';
		case invalidFacetIDs.includes(segmentId):
			return '#FF0000';
		default:
			return 'yellow';
		}
	}, [invalidFacetIDs, isCommonPoint, segmentId]);

	const handleDotDragMoveOptimized = useCallback((e: Konva.KonvaEventObject<DragEvent>, index: number) => {
		const target = e.target as Konva.Circle;
		const editFacetLineGroup = konvaRef?.current?.findOne(`.edit-facet-line-${segmentId}`);
		if (!editFacetLineGroup) return;

		let targetDotPosition = target.position();
		let absDotPosition = target.getAbsolutePosition();

		const currentLine = konvaRef?.current?.findOne(`#editable-${segmentId}`) as Konva.Line;
		if(!currentLine) return;
		const isValidMove = validateDuringDrag(segmentId, currentLine, editableFacets, konvaRef!);
	
		if(!isValidMove){
			currentLine.fill(INVAILD_FACET_HIGHLIGHT);
			currentLine.stroke(INVAILD_FACET_HIGHLIGHT);
			currentLine.setAttr('valid', false);
		}else{
			currentLine.fill(currentSegment.isEdited ? EDITED_FACET_HIGHLIGHT_COLOR : FACET_HIGHLIGHT_COLOR);
			currentLine.stroke('orange');
			currentLine.setAttr('valid', true);
		}

		const SNAP_RADIUS = 2 * (BASE_RADIUS / zoomLevel);

		// Snap to nearby dots
		for (const dot of allCircles.current) {
			if (dot._id === target._id || activeLinkedIdsRef.current.has(dot._id)) continue;

			const dx = dot.x() - targetDotPosition.x;
			const dy = dot.y() - targetDotPosition.y;
			const distance = Math.hypot(dx, dy);

			if (distance < SNAP_RADIUS) {
				targetDotPosition = dot.position();
				absDotPosition = dot.getAbsolutePosition();
				break;
			}
		}

		// Update dot position
		target.position(targetDotPosition);
		// Update linked lines
		const transform = editFacetLineGroup.getAbsoluteTransform().copy().invert();
		const linkedPos = transform.point(absDotPosition);

		linkedPointsRef.current.forEach((ref) => {
			const { facetLines } = ref;
			if (!facetLines) return;
			const points = facetLines.points(); // clone
			points[ref.pointIndex] = linkedPos.x;
			points[ref.pointIndex + 1] = linkedPos.y;
			facetLines.points(points);
		});

		// Throttle using requestAnimationFrame
		if (!rafMap.current.has(target as Konva.Circle)) {
			const rafID = requestAnimationFrame(() => {
				
				// Batch draw layer
				const layer = target.getLayer();
				if (layer) layer.batchDraw();

				rafMap.current.delete(target);

			});
			rafMap.current.set(target, rafID);
		}
	},[editableFacets, konvaRef, segmentId, zoomLevel]);

	return (<Circle
		ref={currentDotRef}
		key={`dot-${segmentId}-${index}`}
		id={`dot-${segmentId}-${index}`}
		name={'movable-circle'}
		x={transformedPoints[index]}
		y={transformedPoints[index + 1]}
		radius={BASE_RADIUS / zoomLevel}
		stroke={(isCommonPoint) ? 'rgb(255, 255, 255)' : 'rgba(0, 0, 0, 0)'}
		strokeWidth={1 / zoomLevel }
		fill={dotFillColor}
		draggable={!isDeletePointsAction && !enableMergeFacets}
		listening={!enableMergeFacets}
		onPointerDown={(e) => {
			dispatch(setPointSelected({isSelected: true}));
		}}
		onPointerUp={() => {
			dispatch(setPointSelected({isSelected: false}));
		}}
		onContextMenu={(e) => {
			e.evt.preventDefault();
		}}
		// perfectDrawEnabled={false}
		onDragStart={(e) => handleDotDragStart(e, index)}
		onDragMove={(e) => handleDotDragMoveOptimized(e, index)}
		onDragEnd={(e) => handleDragEnd(e, index)}
		onClick={(e) => handleDotClick(e, index)}
		onMouseEnter={(e)=>handleHoverOnDot(e, 'ENTER')}
		onMouseLeave={(e)=>handleHoverOnDot(e, 'OUT')}
		onTap={(e)=> handleDotClick(e, index)}
		hitFunc={(ctx, shape) => {
			const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
			const hitRadius = (isTouch ? BASE_RADIUS * 2.5 : BASE_RADIUS)/ zoomLevel;

			ctx.beginPath();
			ctx.arc(0, 0, hitRadius, 0, Math.PI * 2);
			ctx.closePath();
			ctx.fillStrokeShape(shape);
		}}
	/>);
};
export default EditablePoints;